/**
 * Static catalog for the "Open-Source App APIs" section on the Plugins page.
 * Each entry explains how to drive one open-source app (Automatic1111, ComfyUI)
 * through its own HTTP API on a running RunDiffusion session.
 *
 * This is purely presentational content: the whole section is gated in
 * PluginsPage.vue by the USE_RD permission ("Can use Open-Source Apps platform"),
 * the same permission that lets a user launch these apps at all, so anyone who can
 * reach an app's API sees the copy and anyone who can't does not.
 *
 * The CTA on both cards points at OPEN_SOURCE_API_GUIDE_URL (RunDiffusion's own
 * guide covering the API for every open-source app).
 */

// RunDiffusion's own guide to the open-source app APIs. The CTA on every card.
export const OPEN_SOURCE_API_GUIDE_URL = 'https://www.rundiffusion.com/open-source-application-api-guide';

export interface OpenSourceApiCardData {
	// App display name shown as the card title.
	name: string;
	// Short text badge rendered in the card's logo tile (no icon asset needed).
	badge: string;
	// One-word-ish API-shape label with a lead dot, e.g. 'REST API' / 'Node-graph API'.
	apiStyleLabel: string;
	// Hardcoded HTML (paragraphs + a "Highlights" list). Rendered via v-html; safe
	// because it is author-controlled content, never user input.
	description: string;
}

export const OPEN_SOURCE_API_CARDS: OpenSourceApiCardData[] = [
	{
		name: 'Automatic1111',
		badge: 'A1111',
		apiStyleLabel: 'REST API',
		description: `
			<p>Every Automatic1111 session you launch can serve a full REST API. Enable the API on the session, then drive text-to-image and image-to-image generation straight from your own scripts, with no browser in the loop.</p>
			<h4>Highlights</h4>
			<ul>
				<li><strong>Fixed REST endpoints</strong>: POST JSON to <code>/sdapi/v1/txt2img</code> and <code>/sdapi/v1/img2img</code>, and get your images back as base64.</li>
				<li><strong>Self-documenting</strong>: an interactive Swagger reference lives at <code>/docs</code> on every running session.</li>
				<li><strong>Full control</strong>: switch checkpoints, list samplers, poll progress, and post-process, all over HTTP.</li>
			</ul>
		`,
	},
	{
		name: 'ComfyUI',
		badge: 'Comfy',
		apiStyleLabel: 'Node-graph API',
		description: `
			<p>Your ComfyUI sessions speak the same API as the app itself. Export any workflow as API-format JSON, submit it to the server, and automate even complex node graphs from your own code or pipelines.</p>
			<h4>Highlights</h4>
			<ul>
				<li><strong>Queue workflows</strong>: POST your graph to <code>/prompt</code> with a client id and get back a prompt id to track.</li>
				<li><strong>Fetch results</strong>: read <code>/history</code> for the output filenames, then pull each image from <code>/view</code>.</li>
				<li><strong>Live progress</strong>: subscribe to the <code>/ws</code> WebSocket for execution and progress events.</li>
				<li><strong>Discoverable nodes</strong>: <code>/object_info</code> returns the full node schema at runtime.</li>
			</ul>
		`,
	},
];
